import {FC, useEffect, useMemo, useState} from "react";
import {App, ConfigProvider, Empty, Spin, Table, Tag, Typography, theme} from "antd";
import {TrophyOutlined} from "@ant-design/icons";
import AxiosServices from "../Services/axiosServices";
import {contrastColor} from "../Services/color";
import {useGlobal} from "../config/GlobalContext";
import {IGrpCmpEdt} from "../Interfaces/IGrpCmpEdt";
import {IMatch} from "../Interfaces/IMatch";

const ACCENT = '#203923';
const {Title, Text, Paragraph} = Typography;

type StandingRow = {
    rang: number;
    team_idt: number;
    team: string;
    mp: number;
    w: number;
    d: number;
    l: number;
    gf: number;
    ga: number;
    gd: number;
    pts: number;
};

const noWrap = {whiteSpace: 'nowrap' as const};

const columns = [
    {title: '#', dataIndex: 'rang', width: 36, align: 'center' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {
        title: 'Équipe',
        dataIndex: 'team',
        key: 'team',
        onHeaderCell: () => ({style: noWrap}),
        render: (teamName: string, row: StandingRow) => (
            <div style={{display: 'flex', alignItems: 'center', gap: 8, minWidth: 0}}>
                {row?.team_idt != null && (
                    <img
                        src={`/images/${row.team_idt}.png`}
                        alt={teamName ?? ''}
                        style={{width: 22, height: 22, objectFit: 'contain', flexShrink: 0}}
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
                    />
                )}
                <span
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                    }}
                    title={teamName ?? row?.team ?? ''}
                >
                    {teamName ?? row?.team ?? ''}
                </span>
            </div>
        ),
    },
    {title: 'J', dataIndex: 'mp', width: 40, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {title: 'G', dataIndex: 'w', width: 40, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {title: 'N', dataIndex: 'd', width: 40, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {title: 'P', dataIndex: 'l', width: 40, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {title: 'BP', dataIndex: 'gf', width: 44, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {title: 'BC', dataIndex: 'ga', width: 44, align: 'right' as const, onHeaderCell: () => ({style: noWrap}), onCell: () => ({style: noWrap})},
    {
        title: 'Diff',
        dataIndex: 'gd',
        width: 56,
        align: 'right' as const,
        onHeaderCell: () => ({style: noWrap}),
        onCell: () => ({style: noWrap}),
        render: (v: number) => (v > 0 ? `+${v}` : v),
    },
    {
        title: 'Pts',
        dataIndex: 'pts',
        width: 50,
        align: 'right' as const,
        onHeaderCell: () => ({style: noWrap}),
        onCell: () => ({style: noWrap}),
        render: (v: number) => <Text strong>{v}</Text>,
    },
];

const GroupStandings: FC = () => {
    const {currentCmpEdt, groups} = useGlobal();
    const {message} = App.useApp();
    const [standingsByGroup, setStandingsByGroup] = useState<Record<string, StandingRow[]>>({});
    const [matches, setMatches] = useState<IMatch[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!currentCmpEdt || groups.length === 0) {
            setStandingsByGroup({});
            return;
        }
        const grpGroups = groups.filter(g => !!g.Grp_Cod);
        if (grpGroups.length === 0) {
            setStandingsByGroup({});
            return;
        }
        let cancelled = false;
        setLoading(true);
        Promise.all(
            grpGroups.map(g =>
                AxiosServices.groupStandings(currentCmpEdt, g.Grp_Cod as string)
                    .then(rows => [g.Grp_Cod as string, rows] as const)
                    .catch((err: any) => {
                        console.error('groupStandings failed for', g.Grp_Cod, err);
                        message.error(`Échec chargement classement groupe ${g.Grp_Cod}.`);
                        return [g.Grp_Cod as string, [] as StandingRow[]] as const;
                    }),
            ),
        ).then(results => {
            if (cancelled) return;
            const map: Record<string, StandingRow[]> = {};
            results.forEach(([code, rows]) => { map[code] = rows; });
            // Debug aid: surface the shape of the first row so we can spot
            // missing/renamed fields if the table renders blank cells.
            const firstSample = results.flatMap(([_, rows]) => rows).slice(0, 1)[0];
            if (firstSample) console.log('[GroupStandings] sample row:', firstSample);
            setStandingsByGroup(map);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [currentCmpEdt, groups, message]);

    // Needed to know which group's matches are all flagged finished
    // (used by the "Classement définitif" badge). Independent fetch —
    // matchlist is cheap.
    useEffect(() => {
        if (!currentCmpEdt) {
            setMatches([]);
            return;
        }
        AxiosServices.getMatches(setMatches, currentCmpEdt)
            .catch((err) => console.error('getMatches (GroupStandings) failed', err));
    }, [currentCmpEdt]);

    const groupAllFinished = useMemo(() => {
        const m = new Map<string, boolean>();
        const counts = new Map<string, {total: number; finished: number}>();
        for (const match of matches) {
            const code = match.Grp_Cod;
            if (!code) continue;
            // Skip knockout-stage matches: any match assigned via the
            // admin bracket (e.g. A1 vs A2 in QF) shares a Grp_Cod
            // with the group phase, but its 'finished' flag is
            // independent. Counting it postpones the "Classement
            // définitif" badge to after the bracket is over.
            // 'G' = phase de groupe; KO phases use other codes.
            if ((match.Phs_Cod ?? 'G').trim().toUpperCase() !== 'G') continue;
            const c = counts.get(code) ?? {total: 0, finished: 0};
            c.total += 1;
            if (Number(match.finished ?? 0) === 1) c.finished += 1;
            counts.set(code, c);
        }
        counts.forEach((c, code) => m.set(code, c.total > 0 && c.finished === c.total));
        return m;
    }, [matches]);

    if (!currentCmpEdt) {
        return (
            <div style={{padding: 24}}>
                <Empty description="Sélectionne d'abord une compétition dans la sidebar."/>
            </div>
        );
    }

    const renderable = groups.filter(g => !!g.Grp_Cod);

    return (
        <div style={{padding: 24, background: ACCENT, minHeight: '100%'}}>
            <div style={{maxWidth: 1280, margin: '0 auto'}}>
                <Title level={2} style={{color: '#fff', textAlign: 'center'}}>
                    <TrophyOutlined/> Classement réel des groupes
                </Title>
                <Paragraph style={{textAlign: 'center', marginBottom: 24, color: '#cfd8d3'}}>
                    Calculé à partir des scores officiels saisis pour chaque match.
                </Paragraph>

                {loading ? (
                    <div style={{textAlign: 'center', padding: 48}}><Spin/></div>
                ) : renderable.length === 0 ? (
                    <Empty description={<span style={{color: '#cfd8d3'}}>Aucun groupe configuré pour cette compétition.</span>}/>
                ) : (
                    <ConfigProvider theme={{algorithm: theme.darkAlgorithm}}>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
                                gap: 16,
                                alignItems: 'start',
                            }}
                        >
                            {renderable.map((g: IGrpCmpEdt) => {
                                const rows = standingsByGroup[g.Grp_Cod as string] ?? [];
                                const allFinished = groupAllFinished.get(g.Grp_Cod as string) === true;
                                return (
                                    <div
                                        key={g.Idt}
                                        style={{
                                            background: 'rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: 10,
                                            padding: 12,
                                        }}
                                    >
                                        <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
                                            <Tag
                                                style={{
                                                    margin: 0,
                                                    fontWeight: 700,
                                                    minWidth: 26,
                                                    textAlign: 'center',
                                                    background: g.clr || undefined,
                                                    borderColor: g.clr || undefined,
                                                    color: contrastColor(g.clr),
                                                }}
                                            >
                                                {(g.Grp_Cod ?? '').slice(0, 1).toUpperCase()}
                                            </Tag>
                                            <Text strong style={{fontSize: 15, color: '#fff'}}>{g.lib}</Text>
                                            {allFinished && rows.length > 0 && (
                                                <Tag color="gold" style={{margin: 0, marginLeft: 'auto', fontWeight: 700, letterSpacing: 0.5}}>
                                                    Classement définitif
                                                </Tag>
                                            )}
                                        </div>
                                        {rows.length === 0 ? (
                                            <Text italic style={{color: 'rgba(255,255,255,0.55)'}}>
                                                Aucun match joué pour ce groupe.
                                            </Text>
                                        ) : (
                                            <Table
                                                size="small"
                                                pagination={false}
                                                showHeader
                                                dataSource={rows}
                                                rowKey="team_idt"
                                                columns={columns as any}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </ConfigProvider>
                )}
            </div>
        </div>
    );
};

export default GroupStandings;
